import { PERMIT2_ADDRESS as PERMIT2_ADDRESSES } from '@arth-s/universal-router-sdk'
import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import { V3_ROUTER_ADDRESS } from 'constants/addresses'
import { AVERAGE_L1_BLOCK_TIME } from 'constants/chainInfo'
import { UNSUPPORTED_V2POOL_CHAIN_IDS } from 'constants/chains'
import { PermitSignature, usePermitAllowance, useUpdatePermitAllowance } from 'hooks/usePermitAllowance'
import { useTokenAllowance, useUpdateTokenAllowance } from 'hooks/useTokenAllowance'
import useInterval from 'lib/hooks/useInterval'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHasPendingApproval, useTransactionAdder } from 'state/transactions/hooks'

enum ApprovalState {
  PENDING,
  SYNCING,
  SYNCED,
}

export enum AllowanceState {
  LOADING,
  REQUIRED,
  ALLOWED,
}

interface AllowanceRequired {
  state: AllowanceState.REQUIRED
  token: Token
  isApprovalLoading: boolean
  approveAndPermit: () => Promise<void>
}

export type Allowance =
  | { state: AllowanceState.LOADING }
  | {
      state: AllowanceState.ALLOWED
      permitSignature?: PermitSignature
    }
  | AllowanceRequired

export default function usePermit2Allowance(amount?: CurrencyAmount<Token>, spender?: string): Allowance {
  const { account } = useWeb3React()
  const token = amount?.currency

  const v3Only = useMemo(
    () => token?.chainId && UNSUPPORTED_V2POOL_CHAIN_IDS.includes(token?.chainId),
    [token?.chainId]
  )
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const PERMIT2_ADDRESS = v3Only ? V3_ROUTER_ADDRESS[token?.chainId!] : PERMIT2_ADDRESSES(token?.chainId ?? 81)
  const { tokenAllowance, isSyncing: isApprovalSyncing } = useTokenAllowance(token, account, PERMIT2_ADDRESS)
  const updateTokenAllowance = useUpdateTokenAllowance(amount, PERMIT2_ADDRESS)
  const isApproved = useMemo(() => {
    if (!amount || !tokenAllowance) return false
    return tokenAllowance.greaterThan(amount) || tokenAllowance.equalTo(amount)
  }, [amount, tokenAllowance])

  // Marks approval as loading from the time it is submitted (pending), until it has confirmed and another block synced.
  // This avoids re-prompting the user for an already-submitted but not-yet-observed approval, by marking it loading
  // until it has been re-observed. It wll sync immediately, because confirmation fast-forwards the block number.
  const [approvalState, setApprovalState] = useState(ApprovalState.SYNCED)
  const isApprovalLoading = approvalState !== ApprovalState.SYNCED
  const isApprovalPending = useHasPendingApproval(token, PERMIT2_ADDRESS)
  useEffect(() => {
    if (isApprovalPending) {
      setApprovalState(ApprovalState.PENDING)
    } else {
      setApprovalState((state) => {
        if (state === ApprovalState.PENDING && isApprovalSyncing) {
          return ApprovalState.SYNCING
        } else if (state === ApprovalState.SYNCING && !isApprovalSyncing) {
          return ApprovalState.SYNCED
        }
        return state
      })
    }
  }, [isApprovalPending, isApprovalSyncing])

  // Signature and PermitAllowance will expire, so they should be rechecked at an interval.
  // Calculate now such that the signature will still be valid for the submitting block.
  const [now, setNow] = useState((Date.now() + AVERAGE_L1_BLOCK_TIME) / 1000)
  useInterval(
    useCallback(() => setNow((Date.now() + AVERAGE_L1_BLOCK_TIME) / 1000), []),
    !v3Only ? AVERAGE_L1_BLOCK_TIME : null
  )

  const [signature, setSignature] = useState<PermitSignature>()
  const isSigned = useMemo(() => {
    if (!amount || !signature) return false
    return signature.details.token === token?.address && signature.spender === spender && signature.sigDeadline >= now
  }, [amount, now, signature, spender, token?.address])

  const { permitAllowance, expiration: permitExpiration, nonce } = usePermitAllowance(token, account, spender)
  const updatePermitAllowance = useUpdatePermitAllowance(token, spender, nonce, setSignature)
  const isPermitted = useMemo(() => {
    if (!amount || !permitAllowance || !permitExpiration) return false
    return (permitAllowance.greaterThan(amount) || permitAllowance.equalTo(amount)) && permitExpiration >= now
  }, [amount, now, permitAllowance, permitExpiration])

  const shouldRequestApproval = !(isApproved || isApprovalLoading)
  const shouldRequestSignature = !(isPermitted || isSigned) && !v3Only
  const addTransaction = useTransactionAdder()
  const approveAndPermit = useCallback(async () => {
    if (shouldRequestApproval) {
      const { response, info } = await updateTokenAllowance()
      addTransaction(response, info)
    }
    if (shouldRequestSignature) {
      await updatePermitAllowance()
    }
  }, [addTransaction, shouldRequestApproval, shouldRequestSignature, updatePermitAllowance, updateTokenAllowance])

  return useMemo(() => {
    if (token) {
      if (!tokenAllowance || (!permitAllowance && !v3Only)) {
        return { state: AllowanceState.LOADING }
      } else if (!(isPermitted || isSigned) && !v3Only) {
        return { token, state: AllowanceState.REQUIRED, isApprovalLoading: false, approveAndPermit }
      } else if (!isApproved) {
        return { token, state: AllowanceState.REQUIRED, isApprovalLoading, approveAndPermit }
      }
    }
    return {
      token,
      state: AllowanceState.ALLOWED,
      permitSignature: !v3Only && !isPermitted && isSigned ? signature : undefined,
    }
  }, [
    approveAndPermit,
    isApprovalLoading,
    isApproved,
    isPermitted,
    isSigned,
    permitAllowance,
    signature,
    token,
    tokenAllowance,
  ])
}
